import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import Reveal from "@/components/Reveal";
import { ENGAGEMENT_MODELS, PROCESS } from "@/lib/data";

const Pricing = () => (
  <>
    <section className="relative pt-40 pb-20 md:pt-52 border-b border-white/5" data-testid="pricing-hero">
      <div className="max-w-[1480px] mx-auto px-6 md:px-10">
        <Reveal>
          <div className="font-mono-tech text-[11px] tracking-[0.22em] uppercase text-[var(--gold)] mb-6">
            <span className="text-white/35 mr-3">$ / 00</span>How we engage
          </div>
          <h1 className="font-display text-6xl md:text-[7.5vw] leading-[0.92] tracking-[-0.03em] text-white max-w-5xl">
            Three ways to<br />
            work with <span className="font-serif-display text-[var(--gold)]">Lucra</span>.
          </h1>
          <p className="mt-10 max-w-2xl text-white/65 text-lg leading-relaxed">
            Every project is shaped around your venue, your day, your brand. We don’t publish rate cards — we build
            proposals. Pick the engagement model that fits, and we’ll send a tailored quote within 48 hours.
          </p>
        </Reveal>
      </div>
    </section>

    <section className="py-20 md:py-32">
      <div className="max-w-[1480px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-3 gap-px bg-white/5">
        {ENGAGEMENT_MODELS.map((t, i) => (
          <Reveal key={t.name} delay={i * 0.1}>
            <div
              className={`relative p-10 h-full beam-card transition-colors ${
                t.featured ? "bg-[#0c0c0c] md:scale-[1.02]" : "bg-[#0a0a0a]"
              }`}
              data-testid={`pricing-tier-${t.name.toLowerCase()}`}
            >
              <div className="absolute top-0 right-0 font-mono-tech text-[10px] tracking-[0.22em] uppercase text-[var(--gold)] border-l border-b border-[var(--gold)]/40 px-3 py-1 bg-[var(--gold)]/5">
                {t.label}
              </div>
              <div className="font-mono-tech text-xs text-[var(--cyan)]">0{i + 1} · MODEL</div>
              <div className="mt-6 font-display text-5xl text-white tracking-tight">{t.name}</div>

              <p className="mt-6 text-white/65 leading-relaxed">{t.summary}</p>

              <ul className="mt-8 space-y-3">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-white/75 text-sm">
                    <Check size={14} className="text-[var(--gold)] mt-0.5 shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <Link
                to="/contact"
                className={`mt-10 w-full justify-center ${t.featured ? "btn-gold" : "btn-ghost-light"}`}
                data-testid={`pricing-cta-${t.name.toLowerCase()}`}
              >
                <span>Request Proposal</span><ArrowUpRight size={14} />
              </Link>
            </div>
          </Reveal>
        ))}
      </div>
    </section>

    {/* Partnership detail */}
    <section className="py-20 md:py-28 border-t border-white/5" data-testid="pricing-partnership">
      <div className="max-w-[1480px] mx-auto px-6 md:px-10">
        <div className="grid md:grid-cols-12 gap-10 items-start">
          <Reveal className="md:col-span-5">
            <div className="font-mono-tech text-[11px] tracking-[0.22em] uppercase text-[var(--gold)] mb-4">
              <span className="text-white/35 mr-3">$ / 01</span>Partnership Program
            </div>
            <h2 className="font-display text-4xl md:text-5xl text-white tracking-tight leading-[1.05]">
              Zero overhead.<br />Pure upside.
            </h2>
            <p className="mt-6 text-white/65 leading-relaxed">
              The Partnership Program operates on a revenue-share model. We bring the team, the printers, the frames, and
              the merchant flow. Your venue brings the night. Guests purchase the keepsakes directly from us. Your books
              never have to enter the loop.
            </p>
          </Reveal>

          <div className="md:col-span-7 grid sm:grid-cols-2 gap-px bg-white/5">
            {PROCESS.map((p, i) => (
              <Reveal key={p.tag} delay={i * 0.08}>
                <div className="bg-[#0a0a0a] p-8 h-full beam-card">
                  <div className="font-mono-tech text-xs text-[var(--cyan)] mb-4">{p.tag}</div>
                  <div className="font-display text-2xl text-white">{p.title}</div>
                  <p className="mt-3 text-sm text-white/60 leading-relaxed">{p.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>

    {/* What's included strip */}
    <section className="py-20 md:py-28 border-t border-white/5">
      <div className="max-w-[1480px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10">
        <Reveal className="md:col-span-4">
          <div className="font-mono-tech text-[11px] tracking-[0.22em] uppercase text-[var(--gold)] mb-4">
            <span className="text-white/35 mr-3">$ / 02</span>Always included
          </div>
          <h2 className="font-display text-3xl md:text-5xl text-white tracking-tight leading-tight">
            Same craft.<br />Different scale.
          </h2>
        </Reveal>
        <div className="md:col-span-8 grid sm:grid-cols-2 gap-x-10 gap-y-8 text-white/70">
          {[
            ["Pre-production", "Mood boards, shot lists, location scouts, vendor coordination."],
            ["Lead photographer", "Always one of our principal photographers — never outsourced."],
            ["Color science", "Custom-built color grade, OLED-ready, archival-quality exports."],
            ["On-site framing", "Mobile print &amp; frame station for partnership nights."],
            ["Fast turnaround", "Same-night for venue prints. Editorial galleries delivered fast."],
            ["Travel", "ATL ↔ DC included. Anywhere else, transparently itemized."],
          ].map(([h, b]) => (
            <Reveal key={h}>
              <div>
                <div className="font-display text-xl text-white">{h}</div>
                <p className="mt-2 text-sm leading-relaxed text-white/55" dangerouslySetInnerHTML={{ __html: b }} />
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* CTA strip */}
    <section className="py-20 md:py-28 border-t border-white/5">
      <div className="max-w-[1480px] mx-auto px-6 md:px-10 flex flex-col md:flex-row items-start md:items-end justify-between gap-6">
        <Reveal>
          <h3 className="font-display text-3xl md:text-5xl text-white tracking-tight max-w-2xl">
            Ready for a <span className="font-serif-display text-[var(--gold)]">tailored</span> proposal?
          </h3>
        </Reveal>
        <Reveal delay={0.1}>
          <Link to="/contact" className="btn-gold" data-testid="pricing-final-cta"><span>Inquire Now</span><ArrowUpRight size={14} /></Link>
        </Reveal>
      </div>
    </section>
  </>
);

export default Pricing;
